import { Api } from 'further-types/investor';
import { investorTypeLabel } from 'further-ui/labels';
import { dateToLabel } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import { Button } from '@mui/material';
import AlertDialog from 'components/AlertDialog';
import { Row } from 'components/Layout';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

interface Props {
  certification: Api.GetInvestorCertificationResponse[number];
  onReset: () => void;
  onDownload: (certification: Props['certification']) => void;
  isResetting: boolean;
}

const CurrentCertificationResult: React.FC<Props> = ({
  certification,
  onReset,
  isResetting,
  onDownload,
}) => {
  const resetDisclosure = useDisclosure();
  const [canEdit] = useHasPermissions([RolePermission.EditInvestor]);

  return (
    <Row centered spacing="xs">
      <div>
        {certification.createdBy?.adminUser
          ? `Manually classified as ${
              investorTypeLabel[certification.investorType]
            } by ${certification.createdByUser?.firstName} ${
              certification.createdByUser?.lastName
            } on ${dateToLabel(certification.certificationPerformedAt)}`
          : `${investorTypeLabel[certification.investorType]} ${dateToLabel(
              certification.certificationPerformedAt,
              undefined,
              true,
            )}`}
      </div>

      {certification.createdBy?.investor && (
        <Button
          size="extra-small"
          variant="outlined"
          color="primary"
          onClick={() => {
            onDownload(certification);
          }}
        >
          Download investor certification
        </Button>
      )}

      {canEdit && (
        <Button
          size="extra-small"
          variant="outlined"
          color="primary"
          disabled={isResetting}
          onClick={resetDisclosure.onOpen}
        >
          Reset certification status
        </Button>
      )}

      <AlertDialog
        onClose={resetDisclosure.onClose}
        open={resetDisclosure.isOpen}
        title="Warning"
        content="Are you sure you wish to reset this user’s investor certification? This cannot be undone and they will be requested to re-classify themselves when they next make a subscription."
        btnLabels={{ confirm: 'Reset', cancel: 'Go back' }}
        confirmBtnProps={{
          variant: 'contained',
          color: 'primary',
          disabled: isResetting,
        }}
        onConfirm={() => {
          onReset();
          resetDisclosure.onClose();
        }}
      />
    </Row>
  );
};

export default CurrentCertificationResult;
